import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authService } from '../api-services'
import { navigationService } from '../navigation'
import type { AuthState, LoginCredentials, RegisterCredentials } from '@/types'

interface AuthResponse {
  success: boolean
  error?: string
}

interface AuthStore extends AuthState {
  // Actions
  login: (_credentials: LoginCredentials) => Promise<AuthResponse>
  register: (_credentials: RegisterCredentials) => Promise<AuthResponse>
  logout: () => void
  clearAuth: () => void
  setLoading: (_loading: boolean) => void
  sendMagicLink: (_email: string) => Promise<AuthResponse>
  consumeMagicLinkToken: (_token: string) => Promise<AuthResponse>
  // Internal state (not persisted)
  isRehydrated: boolean
  isSendingMagicLink: boolean
  isConsumingMagicLink: boolean
  lastError: string | null
  hasCompleteAuthState: () => boolean
  clearError: () => void
}

// Create auth store with persistence
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      // Non-persisted state for request deduplication and rehydration
      isRehydrated: false,
      isSendingMagicLink: false,
      isConsumingMagicLink: false,
      lastError: null,

      // Helper function to check for complete auth state
      hasCompleteAuthState: (): boolean => {
        const state = get()
        // Check if we have a valid token in localStorage
        const token = typeof window !== 'undefined' ? localStorage.getItem('auth-token') : null
        if (!token) {
          return false
        }

        // Validate token expiration
        try {
          const payload = JSON.parse(atob(token.split('.')[1]))
          const currentTime = Date.now() / 1000
          const isTokenExpired = payload.exp && payload.exp < currentTime

          if (isTokenExpired) {
            // Token is expired
            if (process.env.NODE_ENV === 'production') {
              console.log('Auth state validation failed: Token expired')
            }
            return false
          }
        } catch {
          // Token is malformed
          if (process.env.NODE_ENV === 'production') {
            console.log('Auth state validation failed: Malformed token')
          }
          return false
        }

        const isComplete = state.isAuthenticated &&
                          !!state.user &&
                          !!state.user.id &&
                          !!state.user.email &&
                          !!state.user.name &&
                          !!state.user.role

        // Additional validation for production debugging
        if (process.env.NODE_ENV === 'production' && state.isAuthenticated && !isComplete) {
          console.log('Auth state validation failed:', {
            isAuthenticated: state.isAuthenticated,
            hasUser: !!state.user,
            hasId: !!state.user?.id,
            hasEmail: !!state.user?.email,
            hasName: !!state.user?.name,
            hasRole: !!state.user?.role,
            hasToken: !!token,
          })
        }

        return isComplete
      },

      // Login action
      login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
        set({ isLoading: true, lastError: null })

        try {
          const response = await authService.login(credentials)

          if (response.success && response.data) {
            set({
              user: response.data.user,
              isAuthenticated: true,
              isLoading: false,
              lastError: null,
            })

            // Production debugging
            if (process.env.NODE_ENV === 'production') {
              console.log('Auth Store: Login successful', {
                userId: response.data.user.id,
                userEmail: response.data.user.email,
                userRole: response.data.user.role,
                isAuthenticated: true,
                hasCompleteState: get().hasCompleteAuthState(),
              })
            }

            return { success: true }
          } else {
            const errorMessage = response.error || 'Login failed'
            set({
              isLoading: false,
              lastError: errorMessage,
            })
            console.error('Login failed:', response.error)

            // Production debugging
            if (process.env.NODE_ENV === 'production') {
              console.log('Auth Store: Login failed', response.error)
            }

            return { success: false, error: errorMessage }
          }
        } catch (error) {
          const errorMessage = (error as Error)?.message || 'Login failed'
          set({
            isLoading: false,
            lastError: errorMessage,
          })
          console.error('Login error:', error)

          // Production debugging
          if (process.env.NODE_ENV === 'production') {
            console.log('Auth Store: Login exception', error)
          }

          return { success: false, error: errorMessage }
        }
      },

      // Register action
      register: async (credentials: RegisterCredentials): Promise<AuthResponse> => {
        set({ isLoading: true })

        try {
          const response = await authService.register(credentials)

          if (response.success && response.data) {
            set({
              user: response.data.user,
              isAuthenticated: true,
              isLoading: false,
            })
            return { success: true }
          } else {
            set({ isLoading: false })
            console.error('Registration failed:', response.error)
            return { success: false, error: response.error }
          }
        } catch (error) {
          set({ isLoading: false })
          console.error('Registration error:', error)
          return { success: false, error: (error as Error)?.message || 'Registration failed' }
        }
      },

      // Logout action
      logout: (): void => {
        // Clear auth token from localStorage
        if (typeof window !== 'undefined') {
          localStorage.removeItem('auth-token')

          // Production debugging
          if (process.env.NODE_ENV === 'production') {
            console.log('Logout: Cleared auth token from localStorage')
          }
        }

        // Clear auth state
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        })

        // Navigate to login page using auth-aware navigation
        navigationService.navigateAfterLogout()
      },


      // Clear authentication state
      clearAuth: (): void => {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('auth-token')
        }
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        })
      },

      // Set loading state
      setLoading: (loading: boolean): void => {
        set({ isLoading: loading })
      },

      // Send magic link
      sendMagicLink: async (email: string): Promise<AuthResponse> => {
        set({ isSendingMagicLink: true, lastError: null })

        try {
          const response = await authService.sendMagicLink(email)

          if (response.success) {
            set({
              isSendingMagicLink: false,
              lastError: null,
            })
            return { success: true }
          } else {
            const errorMessage = response.error || 'Failed to send magic link'
            set({
              isSendingMagicLink: false,
              lastError: errorMessage,
            })
            console.error('Magic link failed:', response.error)
            return { success: false, error: errorMessage }
          }
        } catch (error) {
          const errorMessage = (error as Error)?.message || 'Failed to send magic link'
          set({
            isSendingMagicLink: false,
            lastError: errorMessage,
          })
          console.error('Magic link error:', error)
          return { success: false, error: errorMessage }
        }
      },

      // Consume magic link token
      consumeMagicLinkToken: async (token: string): Promise<AuthResponse> => {
        set({ isConsumingMagicLink: true, lastError: null })

        try {
          const response = await authService.consumeMagicLink(token)

          if (response.success && response.data) {
            set({
              user: response.data.user,
              isAuthenticated: true,
              isConsumingMagicLink: false,
              lastError: null,
            })
            return { success: true }
          } else {
            const errorMessage = response.error || 'Failed to authenticate with magic link'
            set({
              isConsumingMagicLink: false,
              lastError: errorMessage,
            })
            console.error('Magic link token consumption failed:', response.error)
            return { success: false, error: errorMessage }
          }
        } catch (error) {
          const errorMessage = (error as Error)?.message || 'Failed to authenticate with magic link'
          set({
            isConsumingMagicLink: false,
            lastError: errorMessage,
          })
          console.error('Magic link token consumption error:', error)
          return { success: false, error: errorMessage }
        }
      },


      // Clear error state
      clearError: (): void => {
        set({ lastError: null })
      },
    }),
    {
      name: 'auth-store',
      // Only persist user and auth status, not loading states
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      // Add hydration handler to prevent SSR mismatches
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Ensure loading state is false and mark as rehydrated
          state.isLoading = false
          state.isRehydrated = true

          // Validate token on rehydration
          if (typeof window !== 'undefined') {
            const token = localStorage.getItem('auth-token')

            if (token) {
              try {
                // Check if token is expired
                const payload = JSON.parse(atob(token.split('.')[1]))
                const currentTime = Date.now() / 1000
                const isTokenExpired = payload.exp && payload.exp < currentTime

                if (isTokenExpired) {
                  // Token is expired, clear everything
                  localStorage.removeItem('auth-token')
                  localStorage.removeItem('auth-store')
                  state.user = null
                  state.isAuthenticated = false

                  if (process.env.NODE_ENV === 'production') {
                    console.log('Auth Store: Token expired on rehydration, cleared auth state')
                  }
                  return
                }
              } catch {
                // Token is malformed, clear everything
                localStorage.removeItem('auth-token')
                localStorage.removeItem('auth-store')
                state.user = null
                state.isAuthenticated = false

                if (process.env.NODE_ENV === 'production') {
                  console.log('Auth Store: Malformed token on rehydration, cleared auth state')
                }
                return
              }
            } else if (state.isAuthenticated) {
              // No token but state says authenticated - clear stale state
              state.user = null
              state.isAuthenticated = false

              if (process.env.NODE_ENV === 'production') {
                console.log('Auth Store: No token but authenticated state, cleared stale state')
              }
              return
            }
          }

          const hasComplete = state.hasCompleteAuthState()

          // Production debugging for hydration
          if (process.env.NODE_ENV === 'production') {
            console.log('Auth Store: Rehydrated', {
              isAuthenticated: state.isAuthenticated,
              hasUser: !!state.user,
              userId: state.user?.id,
              hasCompleteState: hasComplete,
              isRehydrated: true,
            })
          }

          // Note: Removed automatic loadUser() call to prevent unnecessary API requests
          // The loadUser() should only be called explicitly on app initialization
          // or when specifically needed, not on every rehydration
        }
      },
    }
  )
)
